import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getAuth, verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth'
import AuthLayout from './AuthLayout'
import CreatePassWordIcon from '../../assets/auth/create-password.svg'
import { Eye, EyeOff } from 'lucide-react'

const CreatePassword = () => {
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [tokenValid, setTokenValid] = useState<boolean>(false);
  const [validating, setValidating] = useState<boolean>(true);
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = searchParams.get('oobCode');

  const validateToken = useCallback(async () => {
    if (!oobCode) {
      setError("Invalid or missing reset code");
      setValidating(false);
      return;
    }

    try {
      const auth = getAuth();
      // Verify the password reset code is valid
      await verifyPasswordResetCode(auth, oobCode);
      setTokenValid(true);
    } catch (err) {
      console.error("Error validating reset code:", err);
      setError("This reset link is invalid or has expired. Please request a new one.");
      setTokenValid(false);
    } finally {
      setValidating(false);
    }
  }, [oobCode]);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  const validatePassword = (password: string): boolean => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
    return passwordRegex.test(password);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validatePassword(password)) {
      setError("Password must be at least 8 characters with uppercase, lowercase, number, and special character");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!oobCode) {
      setError("Invalid reset code");
      return;
    }

    setLoading(true);

    try {
      const auth = getAuth();
      
      // Reset password using Firebase Auth with oobCode
      await confirmPasswordReset(auth, oobCode, password);

      // Redirect to success page
      navigate('/password-success');
    } catch (err: unknown) {
      console.error("Error resetting password:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to reset password. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }
  if (validating) {
    return (
      <AuthLayout>
        <div className="flex items-center justify-center min-h-screen bg-white">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F15D18] mx-auto"></div>
            <p className="mt-4 text-[#696969] nunito-regular">Validating reset link...</p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (!tokenValid) {
    return (
      <AuthLayout>
        <div className="flex items-center justify-center min-h-screen bg-white">
          <div className="text-center max-w-md">
            <div className="text-[#D5332A] text-5xl mb-4">⚠️</div>
            <h2 className="text-[32px] nunito-extrabold text-[#0A0A0A] mb-4">Invalid Reset Link</h2>
            <p className="text-[#696969] nunito-regular mb-6">{error}</p>
            <button
              onClick={() => navigate('/forgot-passoword')}
              className="px-6 py-3 bg-[#F15D18] text-white nunito-semibold rounded-lg hover:bg-primary"
            >
              Request New Link
            </button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="flex items-center justify-center bg-white">
        <div className="flex-1 flex flex-col justify-center p-8 items-center">
          <div className="space-y-8">
            <div className="text-center">
              <img
                src={CreatePassWordIcon}
                alt="Create Password"
                className="mx-auto w-[235px] h-[181px]"
              />
              
              <div className="flex justify-center items-center gap-5 mt-4">
                <h2 className="text-title flex text-[48px] nunito-extrabold text-[#0A0A0A]">
                  Create New Password
                </h2>
              </div>

              <p className="mt-2 text-subtitle text-[#696969] text-center text-[24px] nunito-regular max-w-[500px] mx-auto">
                Enter your new password below to complete the process
              </p>
            </div>

            <form className="space-y-4 mt-6 flex flex-col items-center" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <label
                  htmlFor="new-password"
                  className="block text-[14px] nunito-semibold text-[#1D1D1D]"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    className="w-[320px] px-4 py-2 pr-10 text-[#919191] text-[16px] border border-[#D9D9D9] rounded-[6px] focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? (
                      <Eye className="h-[24px] w-[24px] text-[#919191]" />
                    ) : (
                      <EyeOff className="h-[24px] w-[24px] text-[#919191]" />
                    )}
                  </button>
                </div>
                <p className="text-[12px] w-[320px] text-[#919191] nunito-medium">
                  Password must be minimum 8 characters, with uppercase, lowercase, a number (0-9), and a special character (!, @, #, $, %, ^, &, *)
                </p>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="confirm-password"
                  className="block text-[14px] nunito-semibold text-[#1D1D1D]"
                >
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    className="w-[320px] px-4 py-2 pr-10 text-[#919191] text-[16px] border border-[#D9D9D9] rounded-[6px] focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Confirm password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  >
                    {showConfirmPassword ? (
                      <Eye className="h-[24px] w-[24px] text-[#919191]" />
                    ) : (
                      <EyeOff className="h-[24px] w-[24px] text-[#919191]" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-[#D5332A] text-[14px] nunito-medium mt-2 w-[320px] text-center">
                  {error}
                </div>
              )}

              <div className="flex flex-col items-center mt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className={`mt-2 w-[320px] h-[40px] flex items-center justify-center px-4 py-2 nunito-bold text-white font-medium rounded-[8px] ${
                    loading ? "bg-gray-400 cursor-not-allowed" : "bg-[#F15D18] hover:bg-primary"
                  } focus:ring-2 focus:ring-orange-500`}
                >
                  {loading ? "Resetting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}

export default CreatePassword
